import React, { useEffect, useState } from 'react';
import { apiFetch } from '../App';
import { Button } from './ui/button';
import { toast } from 'sonner';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  RefreshCw,
  Sparkles,
  Wallet,
  Activity,
} from 'lucide-react';

/*
 * AdminAtlasCloudStatus
 * ─────────────────────
 * Mirror of AdminPayPalStatus for the AtlasCloud AI stack. Lets the admin
 * verify — WITHOUT SSHing — which key the running backend picked up, how
 * much Atlas credit is left, which models are wired for text/vision/STT,
 * and (with one click) whether LLM + STT + Image generation actually work
 * against the live Atlas endpoint.
 *
 * Never shows the raw API key — only presence + a short preview.
 */

const Row = ({ label, value, ok, mono = true }) => (
  <div className="flex items-center justify-between py-1.5 text-sm">
    <span className="text-zinc-400">{label}</span>
    <span className={`flex items-center gap-2 ${mono ? 'font-mono' : ''} text-zinc-200`}>
      {ok === true && <CheckCircle2 className="h-4 w-4 text-emerald-400" />}
      {ok === false && <XCircle className="h-4 w-4 text-red-400" />}
      {value}
    </span>
  </div>
);

const HealthResultRow = ({ label, result }) => {
  if (!result) return null;
  const ok = result.ok;
  return (
    <div
      className={`flex items-start gap-3 rounded-md border p-3 text-sm ${
        ok
          ? 'bg-emerald-500/10 border-emerald-500/30'
          : 'bg-red-500/10 border-red-500/30'
      }`}
      data-testid={`atlas-health-${label.toLowerCase()}-row`}
    >
      {ok ? (
        <CheckCircle2 className="h-4 w-4 mt-0.5 text-emerald-400 shrink-0" />
      ) : (
        <XCircle className="h-4 w-4 mt-0.5 text-red-400 shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="font-medium text-zinc-100">{label}</div>
          <div className="text-xs text-zinc-400">
            {result.latency_ms != null ? `${result.latency_ms} ms` : ''}
            {result.model ? ` · ${result.model}` : ''}
          </div>
        </div>
        {result.detail && (
          <div className="text-xs text-zinc-300/80 mt-1 break-words">
            {result.detail}
          </div>
        )}
      </div>
    </div>
  );
};

const formatUsd = (obj) => {
  if (!obj) return '—';
  const val = parseFloat(obj.value || '0');
  const cur = (obj.currency || 'usd').toUpperCase();
  if (Number.isNaN(val)) return '—';
  return `$${val.toFixed(2)} ${cur}`;
};

const AdminAtlasCloudStatus = () => {
  const [status, setStatus] = useState(null);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [healthChecking, setHealthChecking] = useState(false);
  const [healthResult, setHealthResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiFetch('/admin/atlascloud-status');
      setStatus(data);
      // Fire the balance fetch in parallel — non-blocking, best-effort.
      if (data?.api_key_set) {
        apiFetch('/admin/atlascloud-status/balance')
          .then(({ data: b }) => setBalance(b))
          .catch(() => setBalance({ ok: false, error: 'Balance fetch failed' }));
      }
    } catch (err) {
      toast.error(
        `Could not read AtlasCloud status: ${err?.message || 'unknown error'}`,
      );
    } finally {
      setLoading(false);
    }
  };

  const runHealthCheck = async () => {
    setHealthChecking(true);
    setHealthResult(null);
    try {
      const { data } = await apiFetch(
        '/admin/atlascloud-status/health-check',
        { method: 'POST' },
      );
      setHealthResult(data);
      if (data?.overall_ok) {
        toast.success('AtlasCloud health check: all 3 layers OK');
      } else {
        toast.error('AtlasCloud health check: one or more layers failed');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'unknown error';
      toast.error(`Health check failed: ${detail}`);
      setHealthResult({ overall_ok: false, error: detail });
    } finally {
      setHealthChecking(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const badgeFor = (s) => {
    if (s === 'active')
      return {
        text: 'ACTIVE',
        cls: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/50',
      };
    if (s === 'fallback')
      return {
        text: 'FALLBACK',
        cls: 'bg-amber-500/20 text-amber-300 border-amber-500/50',
      };
    return {
      text: 'UNCONFIGURED',
      cls: 'bg-red-500/20 text-red-300 border-red-500/50',
    };
  };

  return (
    <div className="glass-card p-6" data-testid="admin-atlascloud-status">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-white flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-purple-400" />
          AtlasCloud AI sanity
        </h3>
        <Button
          size="sm"
          variant="outline"
          onClick={load}
          disabled={loading}
          data-testid="atlas-status-refresh-btn"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
        </Button>
      </div>
      <p className="text-zinc-400 text-sm mb-4">
        Shows which key the running backend actually sees, the models it will
        pick for text / vision / audio, and (on click) whether the pipeline
        works end-to-end. The API key itself is never returned — only presence.
      </p>

      {status ? (
        <>
          {/* Status badge + active provider */}
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <span
              className={`px-3 py-1 rounded-full text-xs font-bold border ${badgeFor(status.status).cls}`}
              data-testid="atlas-status-badge"
            >
              {badgeFor(status.status).text}
            </span>
            <span className="text-xs text-zinc-500">
              Active provider ={' '}
              <code data-testid="atlas-status-provider">{status.active_provider}</code>
            </span>
          </div>

          {/* Balance widget */}
          {balance?.ok && (
            <div
              className="mb-4 flex items-center gap-3 rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3"
              data-testid="atlas-balance-widget"
            >
              <Wallet className="h-5 w-5 text-emerald-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-xs text-zinc-400">Available balance</div>
                <div className="text-lg font-bold text-emerald-300 font-mono">
                  {formatUsd(balance.available)}
                </div>
                <div className="text-[11px] text-zinc-500 mt-0.5">
                  cash {formatUsd(balance.cash)} · bonus {formatUsd(balance.bonus)}
                  {balance.subscription_bonus?.value !== '0.000000' &&
                    ` · sub-bonus ${formatUsd(balance.subscription_bonus)}`}
                </div>
              </div>
            </div>
          )}
          {balance && !balance.ok && (
            <div className="mb-4 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200">
              Could not fetch balance: {balance.error || `HTTP ${balance.http_status || '?'}`}
            </div>
          )}

          {/* Config rows */}
          <div className="divide-y divide-white/5">
            <Row
              label="ATLAS_API_KEY"
              value={status.api_key_preview || 'not set'}
              ok={status.api_key_set}
            />
            <Row label="Text model (ATLAS_TEXT_MODEL)" value={status.models.text} />
            <Row label="Vision model (ATLAS_VISION_MODEL)" value={status.models.vision} />
            <Row label="STT model (ATLAS_STT_MODEL)" value={status.models.stt} />
            <Row label="LLM base URL" value={status.endpoints.llm_base} />
            <Row label="Media base URL" value={status.endpoints.media_base} />
            <Row
              label="OPENAI_API_KEY (fallback)"
              value={status.fallbacks.openai_api_key_set ? 'set' : 'not set'}
              ok={undefined}
            />
            <Row
              label="EMERGENT_LLM_KEY (fallback)"
              value={status.fallbacks.emergent_llm_key_set ? 'set' : 'not set'}
              ok={undefined}
            />
          </div>

          {/* Warnings */}
          {status.warnings?.length > 0 && (
            <div className="mt-4 space-y-2" data-testid="atlas-status-warnings">
              {status.warnings.map((w, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2 text-sm text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-md p-3"
                >
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>{w}</span>
                </div>
              ))}
            </div>
          )}

          {/* Health check */}
          <div className="mt-5 pt-5 border-t border-white/10">
            <div className="flex items-center gap-3 flex-wrap">
              <Button
                onClick={runHealthCheck}
                disabled={healthChecking || !status.api_key_set}
                data-testid="atlas-health-check-btn"
                className="flex items-center gap-2"
              >
                {healthChecking ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Activity className="h-4 w-4" />
                )}
                Test LLM + STT + Image
              </Button>
              <span className="text-xs text-zinc-500">
                Runs 3 tiny live requests. Typical duration: 10–20 s. Cost: &lt;$0.01.
              </span>
            </div>

            {healthChecking && (
              <div className="mt-4 flex items-center gap-2 text-sm text-zinc-400">
                <Loader2 className="h-4 w-4 animate-spin" />
                Running LLM → STT (upload + poll Seed ASR) → Image generation…
              </div>
            )}

            {healthResult && !healthChecking && (
              <div
                className="mt-4 space-y-2"
                data-testid="atlas-health-check-result"
              >
                <HealthResultRow label="LLM" result={healthResult.llm} />
                <HealthResultRow label="STT" result={healthResult.stt} />
                <HealthResultRow label="Image" result={healthResult.image} />
                {healthResult.overall_ok === false && healthResult.error && (
                  <div className="rounded-md border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-200">
                    {healthResult.error}
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="flex items-center gap-2 text-zinc-400 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading AtlasCloud status…
        </div>
      )}
    </div>
  );
};

export default AdminAtlasCloudStatus;
